/**
 * Shared PaymentCardForm Component
 * 
 * Consolidates payment card form functionality across merchant-dashboard and customer-portal
 * PCI DSS Level 1 Compliant - Implements address standardization for optimal AVS results
 * 
 * Features:
 * - Address standardization (proper case cities, 2-letter state codes)
 * - Real-time validation with user-friendly feedback
 * - Clean, accessible UI with consistent error handling
 * - Banking-grade input formatting and validation
 * - Configurable theming for different applications
 */

import React, { useState, useCallback, useEffect } from 'react';
import { US_STATES, isValidStateCode } from '../../utils/usStates';
import { 
  formatCityName, 
  isValidZipCode, 
  formatZipCode, 
  formatStreetAddress
} from '../../utils/addressFormatters';
import {
  formatCardNumber,
  validateCardNumber,
  isValidCvv,
  getMaxCardLength,
  getCvvPlaceholder,
  getExpectedCvvLength,
  detectCardBrand
} from '../../utils/cardValidation';

function PaymentCardForm({ 
  onSubmit, 
  processing = false, 
  error = null,
  submitButtonText = "Process Payment",
  theme = 'default'
}) {
  // Form state
  const [formData, setFormData] = useState({
    cardNumber: '',
    cvv: '',
    expiryMonth: '',
    expiryYear: '',
    cardholderName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: ''
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [cardBrand, setCardBrand] = useState(null);

  // Detect card brand when card number changes
  useEffect(() => {
    const brand = detectCardBrand(formData.cardNumber);
    setCardBrand(brand);
    
    // If switching from a 4-digit CVV card (Amex) to 3-digit (or vice versa), clear CVV if it's too long
    if (brand && formData.cvv) {
      const expectedLength = brand.cvvLength;
      if (formData.cvv.length > expectedLength) {
        setFormData(prev => ({ ...prev, cvv: formData.cvv.slice(0, expectedLength) }));
      }
    }
  }, [formData.cardNumber, formData.cvv]);

  // Real-time validation
  const validateField = useCallback((name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'cardNumber':
        const cardValidation = validateCardNumber(value);
        if (!cardValidation.isValid) {
          newErrors.cardNumber = cardValidation.error;
        } else {
          delete newErrors.cardNumber;
        }
        break;

      case 'cvv':
        if (!value) {
          newErrors.cvv = 'CVV is required';
        } else if (!isValidCvv(value, formData.cardNumber)) {
          const expectedLength = getExpectedCvvLength(formData.cardNumber);
          newErrors.cvv = `CVV must be ${expectedLength} digits`;
        } else {
          delete newErrors.cvv;
        }
        break;

      case 'expiryMonth':
        if (!value) {
          newErrors.expiryMonth = 'Month is required';
        } else if (!/^(0[1-9]|1[0-2])$/.test(value)) {
          newErrors.expiryMonth = 'Invalid month';
        } else {
          delete newErrors.expiryMonth;
        }
        break;

      case 'expiryYear':
        const currentYear = new Date().getFullYear();
        const year = parseInt(value);
        if (!value) {
          newErrors.expiryYear = 'Year is required';
        } else if (year < currentYear || year > currentYear + 20) {
          newErrors.expiryYear = 'Invalid expiration year';
        } else {
          delete newErrors.expiryYear;
        }
        break;

      case 'cardholderName':
        if (!value || value.trim().length < 2) {
          newErrors.cardholderName = 'Cardholder name is required';
        } else {
          delete newErrors.cardholderName;
        }
        break;

      case 'billingAddress':
        if (!value || value.trim().length < 5) {
          newErrors.billingAddress = 'Billing address is required (minimum 5 characters)';
        } else {
          delete newErrors.billingAddress;
        }
        break;

      case 'billingCity':
        if (!value || value.trim().length < 2) {
          newErrors.billingCity = 'City is required (minimum 2 characters)';
        } else {
          delete newErrors.billingCity;
        }
        break;

      case 'billingState':
        if (!value) {
          newErrors.billingState = 'State is required';
        } else if (!isValidStateCode(value)) {
          newErrors.billingState = 'Please select a valid state';
        } else {
          delete newErrors.billingState;
        }
        break;

      case 'billingZip':
        if (!value) {
          newErrors.billingZip = 'ZIP code is required';
        } else if (!isValidZipCode(value)) {
          newErrors.billingZip = 'Please enter a valid ZIP code (5 digits or ZIP+4 format)';
        } else {
          delete newErrors.billingZip;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [errors, formData.cardNumber]);

  // Handle input changes with real-time formatting
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Apply formatting based on field type
    switch (name) {
      case 'cardNumber':
        // Use enhanced card number formatting based on detected brand
        const cleanCardInput = value.replace(/\D/g, '');
        const maxCardLength = getMaxCardLength(cleanCardInput);
        formattedValue = formatCardNumber(value).slice(0, maxCardLength);
        
        // Detect card brand immediately as user types
        const detectedBrand = detectCardBrand(formattedValue);
        setCardBrand(detectedBrand);
        break;

      case 'cvv':
        // Only digits, dynamic max length based on card type
        // Use the card brand state for accurate CVV length
        const maxCvvLength = cardBrand ? cardBrand.cvvLength : 4;
        formattedValue = value.replace(/\D/g, '').slice(0, maxCvvLength);
        break;

      case 'expiryMonth':
        // Format as MM
        formattedValue = value.replace(/\D/g, '').slice(0, 2);
        if (formattedValue.length === 2 && parseInt(formattedValue) > 12) {
          formattedValue = '12';
        }
        break;

      case 'expiryYear':
        // Only digits, max 4 characters
        formattedValue = value.replace(/\D/g, '').slice(0, 4);
        break;

      case 'cardholderName':
        // Letters, spaces, and common punctuation only
        formattedValue = value.replace(/[^a-zA-Z\s\-.']/g, '').slice(0, 50);
        break;

      case 'billingAddress':
        // Allow natural typing, only apply length limit
        formattedValue = value.slice(0, 100);
        break;

      case 'billingCity':
        // Allow natural typing, only apply length limit
        formattedValue = value.slice(0, 50);
        break;

      case 'billingZip':
        // Format ZIP code
        formattedValue = formatZipCode(value);
        break;

      default:
        break;
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Validate on change if field was touched
    if (touched[name]) {
      validateField(name, formattedValue);
    }
  };

  // Handle field blur with final formatting
  const handleBlur = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    
    // Apply final formatting on blur
    switch (name) {
      case 'billingAddress':
        finalValue = formatStreetAddress(value);
        break;
      case 'billingCity':
        finalValue = formatCityName(value);
        break;
      case 'billingZip':
        finalValue = formatZipCode(value);
        break;
      default:
        break;
    }
    
    // Update form data with formatted value if it changed
    if (finalValue !== value) {
      setFormData(prev => ({
        ...prev,
        [name]: finalValue
      }));
    }
    
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, finalValue);
  };

  // Handle state selection
  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData(prev => ({ ...prev, billingState: selectedState }));
    setTouched(prev => ({ ...prev, billingState: true }));
    validateField('billingState', selectedState);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.cardNumber || !validateCardNumber(formData.cardNumber).isValid) newErrors.cardNumber = validateCardNumber(formData.cardNumber).error;
    if (!formData.cvv) newErrors.cvv = 'CVV is required';
    else if (!isValidCvv(formData.cvv, formData.cardNumber)) newErrors.cvv = `CVV must be ${getExpectedCvvLength(formData.cardNumber)} digits`;
    if (!formData.expiryMonth || !/^(0[1-9]|1[0-2])$/.test(formData.expiryMonth)) newErrors.expiryMonth = 'Invalid month';
    if (!formData.expiryYear || parseInt(formData.expiryYear) < new Date().getFullYear() || parseInt(formData.expiryYear) > new Date().getFullYear() + 20) newErrors.expiryYear = 'Invalid expiration year';
    if (!formData.cardholderName || formData.cardholderName.trim().length < 2) newErrors.cardholderName = 'Cardholder name is required';
    if (!formData.billingAddress || formData.billingAddress.trim().length < 5) newErrors.billingAddress = 'Billing address is required (minimum 5 characters)';
    if (!formData.billingCity || formData.billingCity.trim().length < 2) newErrors.billingCity = 'City is required (minimum 2 characters)';
    if (!formData.billingState || !isValidStateCode(formData.billingState)) newErrors.billingState = 'Please select a valid state';
    if (!formData.billingZip || !isValidZipCode(formData.billingZip)) newErrors.billingZip = 'Please enter a valid ZIP code (5 digits or ZIP+4 format)';

    setErrors(newErrors);

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (Object.keys(newErrors).length > 0) return;

    try {
      // Clean card number and extract data
      const cleanCardNumber = formData.cardNumber.replace(/\s/g, '');
      const detectedBrandObj = detectCardBrand(cleanCardNumber);
      const detectedBrand = detectedBrandObj ? detectedBrandObj.key : '';
      const last4Digits = cleanCardNumber.slice(-4);
      
      // Prepare data for submission with standardized formatting
      const submissionData = {
        cardNumber: cleanCardNumber, // Remove spaces
        cvv: formData.cvv,
        expiryMonth: formData.expiryMonth.padStart(2, '0'), // Ensure MM format
        expiryYear: formData.expiryYear,
        cardholderName: formData.cardholderName.trim(),
        billingAddress: formatStreetAddress(formData.billingAddress),
        billingCity: formatCityName(formData.billingCity),
        billingState: formData.billingState.toUpperCase(), // Ensure uppercase
        billingZip: formatZipCode(formData.billingZip),
        // Add card metadata for proper processing
        brand: detectedBrand,
        last4: last4Digits
      };

      await onSubmit(submissionData);
    } catch (submitError) {
      console.error('Payment form submission error:', submitError);
      // Error handling will be managed by parent component
    }
  };


  // Theme-based styling
  const getInputClasses = (hasError) => {
    const baseClasses = "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors";
    const errorClasses = hasError ? "border-red-500 focus:ring-red-200" : "border-gray-300 focus:ring-blue-200 focus:border-blue-500";
    const themeClasses = theme === 'portal' ? "bg-gray-50" : "bg-white";
    
    return `${baseClasses} ${errorClasses} ${themeClasses}`;
  };

  const getButtonClasses = () => {
    const baseClasses = "w-full py-3 px-4 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";
    const themeClasses = theme === 'portal' 
      ? "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white" 
      : "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 text-white";
    const disabledClasses = processing ? "opacity-50 cursor-not-allowed" : "";
    
    return `${baseClasses} ${themeClasses} ${disabledClasses}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Global error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Card Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Card Information</h3>
        
        {/* Card Number */}
        <div>
          <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
            Card Number * {cardBrand && <span className="text-xs text-gray-500 ml-2">({cardBrand.name})</span>}
          </label>
          <input
            type="text"
            id="cardNumber"
            name="cardNumber"
            value={formData.cardNumber}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="1234 5678 9012 3456"
            className={getInputClasses(errors.cardNumber && touched.cardNumber)}
            disabled={processing}
          />
          {errors.cardNumber && touched.cardNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.cardNumber}</p>
          )}
        </div>

        {/* Expiry and CVV Row */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label htmlFor="expiryMonth" className="block text-sm font-medium text-gray-700 mb-1">
              Month *
            </label>
            <input
              type="text"
              id="expiryMonth"
              name="expiryMonth"
              value={formData.expiryMonth}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="MM"
              className={getInputClasses(errors.expiryMonth && touched.expiryMonth)}
              disabled={processing}
            />
            {errors.expiryMonth && touched.expiryMonth && (
              <p className="mt-1 text-sm text-red-600">{errors.expiryMonth}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="expiryYear" className="block text-sm font-medium text-gray-700 mb-1">
              Year *
            </label>
            <input
              type="text"
              id="expiryYear"
              name="expiryYear"
              value={formData.expiryYear}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="YYYY"
              className={getInputClasses(errors.expiryYear && touched.expiryYear)}
              disabled={processing}
            />
            {errors.expiryYear && touched.expiryYear && (
              <p className="mt-1 text-sm text-red-600">{errors.expiryYear}</p>
            )}
          </div>

          <div>
            <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
              CVV *
            </label>
            <input
              type="text"
              id="cvv"
              name="cvv"
              value={formData.cvv}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder={getCvvPlaceholder(formData.cardNumber)}
              className={getInputClasses(errors.cvv && touched.cvv)}
              disabled={processing}
            />
            {errors.cvv && touched.cvv && (
              <p className="mt-1 text-sm text-red-600">{errors.cvv}</p>
            )}
          </div>
        </div>

        {/* Cardholder Name */}
        <div>
          <label htmlFor="cardholderName" className="block text-sm font-medium text-gray-700 mb-1">
            Cardholder Name *
          </label>
          <input
            type="text"
            id="cardholderName"
            name="cardholderName"
            value={formData.cardholderName}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="John Doe"
            className={getInputClasses(errors.cardholderName && touched.cardholderName)}
            disabled={processing}
          />
          {errors.cardholderName && touched.cardholderName && (
            <p className="mt-1 text-sm text-red-600">{errors.cardholderName}</p>
          )}
        </div>
      </div>

      {/* Billing Address Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Billing Address</h3>
        
        {/* Street Address */}
        <div>
          <label htmlFor="billingAddress" className="block text-sm font-medium text-gray-700 mb-1">
            Street Address *
          </label>
          <input
            type="text"
            id="billingAddress"
            name="billingAddress"
            value={formData.billingAddress}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="123 Main St"
            className={getInputClasses(errors.billingAddress && touched.billingAddress)}
            disabled={processing}
          />
          {errors.billingAddress && touched.billingAddress && (
            <p className="mt-1 text-sm text-red-600">{errors.billingAddress}</p>
          )}
        </div>

        {/* City and State Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="billingCity" className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <input
              type="text"
              id="billingCity"
              name="billingCity"
              value={formData.billingCity}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Kalispell"
              className={getInputClasses(errors.billingCity && touched.billingCity)}
              disabled={processing}
            />
            {errors.billingCity && touched.billingCity && (
              <p className="mt-1 text-sm text-red-600">{errors.billingCity}</p>
            )}
          </div>

          <div>
            <label htmlFor="billingState" className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              id="billingState"
              name="billingState"
              value={formData.billingState}
              onChange={handleStateChange}
              onBlur={handleBlur}
              className={getInputClasses(errors.billingState && touched.billingState)}
              disabled={processing}
            >
              <option value="">Select State</option>
              {US_STATES.map(state => (
                <option key={state.code} value={state.code}>
                  {state.code} - {state.name}
                </option>
              ))}
            </select>
            {errors.billingState && touched.billingState && (
              <p className="mt-1 text-sm text-red-600">{errors.billingState}</p>
            )}
          </div>
        </div>

        {/* ZIP Code */}
        <div className="w-1/2">
          <label htmlFor="billingZip" className="block text-sm font-medium text-gray-700 mb-1">
            ZIP Code *
          </label>
          <input
            type="text"
            id="billingZip"
            name="billingZip"
            value={formData.billingZip}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="59901"
            className={getInputClasses(errors.billingZip && touched.billingZip)}
            disabled={processing}
          />
          {errors.billingZip && touched.billingZip && (
            <p className="mt-1 text-sm text-red-600">{errors.billingZip}</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={processing}
        className={getButtonClasses()}
      >
        {processing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : (
          submitButtonText
        )}
      </button>

      {/* Security Notice */}
      <div className="text-xs text-gray-500 text-center">
        <p>🔒 Your payment information is encrypted and secure. We never store your card details.</p>
      </div>
    </form>
  );
}

export default PaymentCardForm;